import { useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Modal from '../../../../components/common/Modal';
import ModalButton from '../../../../components/common/ModalButton';
import ToggleChip from '../../../../components/common/ToggleChip';

import { getDetailedRelativeTime } from '../../../../helpers/songDates';
import { formatDate } from '../../../../utils/format';

import { ReactComponent as UserIcon } from '../../../../icons/user.svg';

import './PermissionModal.scss';

const PermissionModal = ({ isOpen, onClose, user, team, onRemoveUser, onToggleSuggestSongs }) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');

  if (!user) return null;

  // What has to be typed to remove someone, in the app's language as the prompt shows it
  const removePhrase = t('team.permissions.phrase', { name: user.firstName });

  const formatJoinDate = (date) => {
    if (!date) return t('team.permissions.notAvailable');
    return formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleRemoveUser = () => {
    if (deleteInput === removePhrase) {
      setShowDeleteConfirm(false);
      setDeleteInput('');
      onRemoveUser(user._id);
      onClose();
    }
  };

  const handleClose = () => {
    setShowDeleteConfirm(false);
    setDeleteInput('');
    onClose();
  };

  const handleInitiateDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteInput('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('team.permissions.title')}
      className="permission-modal-container"
    >
      <div className="permission-modal-content">
        {!showDeleteConfirm ? (
          <>
            <div className="user-info">
              <div className="user-icon">
                <UserIcon />
              </div>
              <h4 className="user-name">
                {user.firstName || t('team.permissions.firstName')} {user.lastName || t('team.permissions.lastName')}
              </h4>
              <p className="joined">
                {t('team.permissions.joined', { date: formatJoinDate(user.joinedDate || user.createdAt) })}
              </p>
              <p className="joined-relative">({getDetailedRelativeTime(user.joinedDate || user.createdAt)})</p>
            </div>

            <div className="permissions">
              <ToggleChip
                name="suggest-songs"
                label={t('team.permissions.suggestSongs')}
                checked={user.permissions?.canSuggestSongs || false}
                onChange={() => onToggleSuggestSongs(user._id)}
              />
            </div>

            <div className="actions">
              <ModalButton variant="danger" block onClick={handleInitiateDelete}>
                {t('team.permissions.removeFrom', { name: user.firstName, team: team.name })}
              </ModalButton>
            </div>
          </>
        ) : (
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3 className="warning-title">{t('team.permissions.removeUser')}</h3>
            <p className="warning-message">
              <Trans
                i18nKey="team.permissions.warning"
                values={{ name: `${user.firstName} ${user.lastName}`.trim(), team: team.name }}
                components={{ strong: <strong /> }}
              />
            </p>
            <div className="confirmation-input">
              <label htmlFor="delete-confirm">
                <Trans
                  i18nKey="team.permissions.typeToConfirm"
                  values={{ phrase: removePhrase }}
                  components={{ strong: <strong /> }}
                />
              </label>
              <input
                id="delete-confirm"
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder={removePhrase}
                autoFocus
              />
            </div>
            <div className="confirmation-actions">
              <ModalButton variant="secondary" onClick={handleCancelDelete}>
                {t('common.cancel')}
              </ModalButton>
              <ModalButton variant="danger" onClick={handleRemoveUser} disabled={deleteInput !== removePhrase}>
                {t('team.permissions.removeUser')}
              </ModalButton>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

PermissionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  team: PropTypes.object.isRequired,
  onRemoveUser: PropTypes.func.isRequired,
  onToggleSuggestSongs: PropTypes.func.isRequired,
};

export default PermissionModal;
